import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatTime, formatTimeFromHhMm } from "../../utils/regionalFormat";
import { useRegionalSettings } from "../../context/RegionalSettingsContext";
import { filterTimeOptionObjects } from "../../utils/timeOptions";

function pad(n) {
    return String(n).padStart(2, "0");
}

// Generate 15-minute interval times between start and end
function generateTimeOptions(sessionStartTime, sessionEndTime) {
    if (!sessionStartTime || !sessionEndTime) return [];

    // Parse session times (format: "YYYY-MM-DDTHH:mm" or similar ISO format)
    const start = new Date(sessionStartTime);
    const end = new Date(sessionEndTime);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];

    const options = [];
    const current = new Date(start);

    // Round to nearest 15 minutes
    const minutes = current.getMinutes();
    const roundedMinutes = Math.floor(minutes / 15) * 15;
    current.setMinutes(roundedMinutes);
    current.setSeconds(0);
    current.setMilliseconds(0);

    // Generate options in 15-minute intervals
    while (current <= end) {
        const hours = current.getHours();
        const mins = current.getMinutes();
        const value = `${pad(hours)}:${pad(mins)}`;

        options.push({
            value,
            label: formatTimeFromHhMm(value),
        });

        current.setMinutes(current.getMinutes() + 15);
    }

    return options;
}

// Convert ISO string to HH:mm format
export function isoToTimeString(isoStr) {
    if (!isoStr) return "";
    const date = new Date(isoStr);
    if (isNaN(date.getTime())) return "";
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function SessionTimePicker({
    value,
    onChange,
    sessionDate,
    sessionStartTime,
    sessionEndTime,
    placeholder,
    className = "",
    /** HH:mm — check-out: only times strictly after check-in */
    minTime = null,
    /** HH:mm — check-in: only times strictly before check-out */
    maxTime = null,
}) {
    const { t } = useTranslation();
    const { settings } = useRegionalSettings();
    const [open, setOpen] = useState(false);
    const [displayValue, setDisplayValue] = useState("");
    const wrapperRef = useRef(null);

    const selectedHhMm = isoToTimeString(value);
    const timeOptions = useMemo(
        () => filterTimeOptionObjects(
            generateTimeOptions(sessionStartTime, sessionEndTime),
            { minTime, maxTime, keepValue: selectedHhMm || null }
        ),
        [sessionStartTime, sessionEndTime, minTime, maxTime, selectedHhMm, settings?.timeFormat]
    );

    useEffect(() => {
        if (value) {
            setDisplayValue(formatTime(value));
        } else {
            setDisplayValue("");
        }
    }, [value, settings?.timeFormat]);

    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleTimeSelect = (timeValue) => {
        // timeValue is HH:mm format
        if (!sessionDate) {
            console.error("Session date is required");
            return;
        }

        const sessionDateObj = new Date(sessionDate);
        if (isNaN(sessionDateObj.getTime())) {
            console.error("Invalid session date");
            return;
        }

        const [hours, minutes] = timeValue.split(':').map(Number);

        // Create local date-time string in format YYYY-MM-DDTHH:mm
        const year = sessionDateObj.getFullYear();
        const month = pad(sessionDateObj.getMonth() + 1);
        const day = pad(sessionDateObj.getDate());
        const localDateTimeString = `${year}-${month}-${day}T${pad(hours)}:${pad(minutes)}`;

        onChange(localDateTimeString);
        setOpen(false);
    };

    const handleClear = () => {
        setDisplayValue("");
        onChange("");
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} className="relative">
            <input
                type="text"
                value={displayValue}
                onClick={() => setOpen(!open)}
                readOnly
                placeholder={placeholder ?? t('datePicker.selectTime')}
                className={className || "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"}
            />

            {open && (
                <div className="absolute z-50 mt-1 p-3 bg-white border border-gray-300 rounded-lg shadow-lg" style={{ minWidth: '200px', maxHeight: '300px' }}>
                    <div className="space-y-2">
                        <div className="text-xs font-medium text-gray-700 mb-2">
                            {t('datePicker.selectTimeIntervals')}
                        </div>

                        {timeOptions.length === 0 ? (
                            <div className="text-sm text-gray-500 py-4 text-center">
                                {t('datePicker.noTimeOptions')}
                            </div>
                        ) : (
                            <div className="max-h-48 overflow-y-auto space-y-1">
                                {timeOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        onClick={() => handleTimeSelect(option.value)}
                                        className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-blue-100 transition-colors ${isoToTimeString(value) === option.value
                                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                                : 'bg-gray-50 text-gray-700'
                                            }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="flex gap-2 pt-2 border-t">
                            <button
                                onClick={() => setOpen(false)}
                                className="flex-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                            >
                                {t('common.done')}
                            </button>
                            <button
                                onClick={handleClear}
                                className="flex-1 px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                            >
                                {t('datePicker.clear')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SessionTimePicker;
